import { command } from 'ccstate'
import { apiClient$ } from '../infra/apiClient'
import { fileExplorerState } from '../state/fileExplorerState'
import type { FileUpload } from '../../common/schema/schemaType'

// Navigation commands
export const navigateToFolder$ = command(({ set }, folderId: string | null) => {
  set(fileExplorerState.currentFolderId$, folderId)
  set(fileExplorerState.selectedFileIds$, [])
})

// File operations
export const createFolder$ = command(async ({ get, set }, name: string) => {
  const apiClient = get(apiClient$)
  const currentFolderId = get(fileExplorerState.currentFolderId$) as string | null

  set(fileExplorerState.isCreatingFolder$, true)

  try {
    await apiClient.postApi('createFolder', {
      name,
      parentId: currentFolderId
    })

    // Refresh files list
    const currentDirty = get(fileExplorerState.filesDirty$) as number
    set(fileExplorerState.filesDirty$, currentDirty + 1)
  } finally {
    set(fileExplorerState.isCreatingFolder$, false)
  }
})

export const uploadFile$ = command(async ({ get, set }, file: FileUpload) => {
  const apiClient = get(apiClient$)
  const currentFolderId = get(fileExplorerState.currentFolderId$) as string | null

  set(fileExplorerState.isUploading$, true)

  try {
    await apiClient.postApi('uploadFile', {
      file,
      parentId: currentFolderId
    })

    // Refresh files list and storage info
    const filesDirty = get(fileExplorerState.filesDirty$) as number
    const storageDirty = get(fileExplorerState.storageDirty$) as number
    set(fileExplorerState.filesDirty$, filesDirty + 1)
    set(fileExplorerState.storageDirty$, storageDirty + 1)
  } finally {
    set(fileExplorerState.isUploading$, false)
  }
})

export const deleteFiles$ = command(async ({ get, set }, fileIds: string[]) => {
  const apiClient = get(apiClient$)

  await apiClient.postApi('deleteFiles', { fileIds })

  // Clear selection and refresh
  set(fileExplorerState.selectedFileIds$, [])
  const filesDirty = get(fileExplorerState.filesDirty$) as number
  const storageDirty = get(fileExplorerState.storageDirty$) as number
  set(fileExplorerState.filesDirty$, filesDirty + 1)
  set(fileExplorerState.storageDirty$, storageDirty + 1)
})

export const moveFiles$ = command(async ({ get, set }, fileIds: string[], targetFolderId: string | null) => {
  const apiClient = get(apiClient$)

  await apiClient.postApi('moveFiles', {
    fileIds,
    targetFolderId
  })

  // Clear selection and refresh
  set(fileExplorerState.selectedFileIds$, [])
  const currentDirty = get(fileExplorerState.filesDirty$) as number
  set(fileExplorerState.filesDirty$, currentDirty + 1)
})

export const renameFile$ = command(async ({ get, set }, fileId: string, newName: string) => {
  const apiClient = get(apiClient$)

  await apiClient.postApi('renameFile', {
    fileId,
    newName
  })

  // Refresh files list
  const currentDirty = get(fileExplorerState.filesDirty$) as number
  set(fileExplorerState.filesDirty$, currentDirty + 1)
})

export const toggleFileStar$ = command(async ({ get, set }, fileId: string, starred: boolean) => {
  const apiClient = get(apiClient$)

  await apiClient.postApi('toggleStar', {
    fileId,
    starred
  })

  // Refresh files list
  const currentDirty = get(fileExplorerState.filesDirty$) as number
  set(fileExplorerState.filesDirty$, currentDirty + 1)
})

// Selection commands
export const selectFile$ = command(({ get, set }, fileId: string) => {
  const currentSelection = get(fileExplorerState.selectedFileIds$) as string[]
  if (!currentSelection.includes(fileId)) {
    set(fileExplorerState.selectedFileIds$, [...currentSelection, fileId])
  }
})

export const deselectFile$ = command(({ get, set }, fileId: string) => {
  const currentSelection = get(fileExplorerState.selectedFileIds$) as string[]
  set(fileExplorerState.selectedFileIds$, currentSelection.filter((id: string) => id !== fileId))
})

export const toggleFileSelection$ = command(({ get, set }, fileId: string) => {
  const currentSelection = get(fileExplorerState.selectedFileIds$) as string[]
  if (currentSelection.includes(fileId)) {
    set(fileExplorerState.selectedFileIds$, currentSelection.filter((id: string) => id !== fileId))
  } else {
    set(fileExplorerState.selectedFileIds$, [...currentSelection, fileId])
  }
})

export const selectAllFiles$ = command(async ({ get, set }) => {
  const currentFiles = await get(fileExplorerState.currentFiles$) as any[]
  const allFileIds = currentFiles.map((file: any) => file.id)
  set(fileExplorerState.selectedFileIds$, allFileIds)
})

export const clearSelection$ = command(({ set }) => {
  set(fileExplorerState.selectedFileIds$, [])
})

// View commands
export const setViewMode$ = command(({ set }, mode: 'grid' | 'list') => {
  set(fileExplorerState.viewMode$, mode)
})

export const setSortOptions$ = command(({ get, set }, sortBy: string, sortDirection: 'asc' | 'desc') => {
  set(fileExplorerState.sortBy$, sortBy as any)
  set(fileExplorerState.sortDirection$, sortDirection)

  // Refresh files with new sort
  const currentDirty = get(fileExplorerState.filesDirty$) as number
  set(fileExplorerState.filesDirty$, currentDirty + 1)
})

export const setSearchQuery$ = command(({ set }, query: string) => {
  set(fileExplorerState.searchQuery$, query)
})

export const toggleDetailsPanel$ = command(({ get, set }) => {
  const current = get(fileExplorerState.showDetailsPanel$)
  set(fileExplorerState.showDetailsPanel$, !current)
})